/**
 * A scoped buffer for structured reads against incoming serial data during
 * a management operation (file transfer, device info query, etc.).
 *
 * When a transaction is active, the serial router feeds all incoming device
 * data into this buffer instead of writing it to the terminal. The transaction
 * provides `readUntil` and `readExactly` primitives that resolve when the
 * requested data pattern arrives, or reject on timeout.
 *
 * On release, any remaining buffered data can be retrieved via `drain()` and
 * forwarded to the terminal if appropriate.
 */
export class SerialTransaction {
    private buffer = '';
    private pendingRead: PendingRead | null = null;

    /**
     * Called by the serial router when data arrives during this transaction.
     * Data is appended to the internal buffer and any pending read is checked.
     */
    feed(data: string): void {
        this.buffer += data;

        if (this.pendingRead) {
            this.tryResolvePending();
        }
    }

    /**
     * Read from the buffer until `ending` appears. Returns everything up to
     * and including the ending string.
     *
     * If the ending is already present in the buffer, resolves immediately.
     * Otherwise waits for new data (via `feed()`) until the ending appears
     * or the timeout expires.
     *
     * @param ending - The string to search for
     * @param timeout - Maximum milliseconds to wait (default 5000)
     * @returns The consumed data including the ending
     * @throws Error if timeout expires or if another read is already pending
     */
    async readUntil(ending: string, timeout = 5000): Promise<string> {
        if (this.pendingRead) {
            throw new Error(
                'SerialTransaction: cannot have multiple concurrent reads',
            );
        }

        // Check if ending is already in the buffer
        const idx = this.buffer.indexOf(ending);
        if (idx >= 0) {
            const result = this.buffer.substring(0, idx + ending.length);
            this.buffer = this.buffer.substring(idx + ending.length);
            return result;
        }

        return new Promise<string>((resolve, reject) => {
            const timer = setTimeout(() => {
                this.pendingRead = null;
                reject(
                    new Error(
                        `SerialTransaction: timeout waiting for "${this.escapeForLog(ending)}"`,
                    ),
                );
            }, timeout);

            this.pendingRead = {
                type: 'until',
                ending,
                resolve: (data: string) => {
                    clearTimeout(timer);
                    this.pendingRead = null;
                    resolve(data);
                },
                reject: (err: Error) => {
                    clearTimeout(timer);
                    this.pendingRead = null;
                    reject(err);
                },
            };
        });
    }

    /**
     * Read exactly `n` characters from the buffer.
     *
     * If enough characters are already buffered, resolves immediately.
     * Otherwise waits for new data (via `feed()`) until `n` characters
     * are available or the timeout expires.
     *
     * @param n - Number of characters to read
     * @param timeout - Maximum milliseconds to wait (default 5000)
     * @returns Exactly `n` characters
     * @throws Error if timeout expires or if another read is already pending
     */
    async readExactly(n: number, timeout = 5000): Promise<string> {
        if (this.pendingRead) {
            throw new Error(
                'SerialTransaction: cannot have multiple concurrent reads',
            );
        }

        // Check if we already have enough data
        if (this.buffer.length >= n) {
            const result = this.buffer.substring(0, n);
            this.buffer = this.buffer.substring(n);
            return result;
        }

        return new Promise<string>((resolve, reject) => {
            const timer = setTimeout(() => {
                this.pendingRead = null;
                reject(
                    new Error(
                        `SerialTransaction: timeout waiting for ${n} characters (have ${this.buffer.length})`,
                    ),
                );
            }, timeout);

            this.pendingRead = {
                type: 'exactly',
                count: n,
                resolve: (data: string) => {
                    clearTimeout(timer);
                    this.pendingRead = null;
                    resolve(data);
                },
                reject: (err: Error) => {
                    clearTimeout(timer);
                    this.pendingRead = null;
                    reject(err);
                },
            };
        });
    }

    /**
     * Discard everything currently in the buffer.
     * Useful after entering raw REPL to clear any leftover prompt data.
     */
    flush(): void {
        this.buffer = '';
    }

    /**
     * Retrieve all remaining buffered data and clear the buffer.
     * Called when the transaction is released so remaining data can be
     * evaluated or forwarded to the terminal.
     */
    drain(): string {
        const remaining = this.buffer;
        this.buffer = '';
        return remaining;
    }

    /**
     * Cancel any pending read operation. Called during abort/error scenarios.
     *
     * This is the primary mechanism for terminating a `readUntilStreaming` call
     * that has no timeout. When the device disconnects or the user triggers a
     * reset, the caller MUST invoke `cancel()` to unblock the pending promise.
     * Without this, `readUntilStreaming` would hang forever.
     *
     * Contract: any code that holds a reference to an active transaction must
     * call `cancel()` if the serial connection is lost or the operation is aborted.
     * See `disconnectDevice` in actors/connection.ts for the disconnect path.
     */
    cancel(): void {
        if (this.pendingRead) {
            this.pendingRead.reject(new Error('SerialTransaction: cancelled'));
            this.pendingRead = null;
        }
        this.buffer = '';
    }

    /**
     * Read from the buffer, streaming each chunk to `onChunk` as it arrives,
     * until `ending` appears. Only holds enough buffer to detect the ending
     * across chunk boundaries — no unbounded accumulation.
     *
     * **No timeout**: This method waits indefinitely for the ending marker.
     * It is designed for long-running scripts where the device may produce
     * output for minutes or hours. Termination paths:
     * - The device sends the ending marker (natural completion or Ctrl-C interrupt)
     * - `cancel()` is called externally (device disconnect, user abort)
     *
     * Callers MUST ensure `cancel()` is called if the connection is lost,
     * otherwise this promise will never resolve.
     *
     * @param ending - The string to search for (e.g., '\x04')
     * @param onChunk - Called with each chunk of data as it arrives (before ending is found)
     */
    async readUntilStreaming(
        ending: string,
        onChunk: (chunk: string) => void,
    ): Promise<string> {
        if (this.pendingRead) {
            throw new Error(
                'SerialTransaction: cannot have multiple concurrent reads',
            );
        }

        // Check if ending is already in the buffer
        const idx = this.buffer.indexOf(ending);
        if (idx >= 0) {
            const before = this.buffer.substring(0, idx);
            if (before.length > 0) onChunk(before);
            this.buffer = this.buffer.substring(idx + ending.length);
            return ending;
        }

        // Forward any existing buffer content (except potential partial ending match)
        const safeLength = Math.max(
            0,
            this.buffer.length - (ending.length - 1),
        );
        if (safeLength > 0) {
            onChunk(this.buffer.substring(0, safeLength));
            this.buffer = this.buffer.substring(safeLength);
        }

        return new Promise<string>((resolve, reject) => {
            this.pendingRead = {
                type: 'streaming',
                ending,
                accumulated: '',
                onChunk,
                resolve: (data: string) => {
                    this.pendingRead = null;
                    resolve(data);
                },
                reject: (err: Error) => {
                    this.pendingRead = null;
                    reject(err);
                },
            };
        });
    }

    /**
     * The current number of buffered characters (for debugging/diagnostics).
     */
    get bufferedLength(): number {
        return this.buffer.length;
    }

    // --- Private ---

    private tryResolvePending(): void {
        if (!this.pendingRead) return;

        if (this.pendingRead.type === 'until') {
            const { ending, resolve } = this.pendingRead;
            const idx = this.buffer.indexOf(ending);
            if (idx >= 0) {
                const result = this.buffer.substring(0, idx + ending.length);
                this.buffer = this.buffer.substring(idx + ending.length);
                resolve(result);
            }
        } else if (this.pendingRead.type === 'exactly') {
            const { count, resolve } = this.pendingRead;
            if (this.buffer.length >= count) {
                const result = this.buffer.substring(0, count);
                this.buffer = this.buffer.substring(count);
                resolve(result);
            }
        } else if (this.pendingRead.type === 'streaming') {
            const { ending, onChunk, resolve } = this.pendingRead;

            // For streaming, we only need to check new data for the ending marker.
            // Forward everything before it immediately — don't accumulate the full history.
            const idx = this.buffer.indexOf(ending);
            if (idx >= 0) {
                // Found the ending — forward everything before it, then resolve
                const before = this.buffer.substring(0, idx);
                if (before.length > 0) {
                    onChunk(before);
                }
                this.buffer = this.buffer.substring(idx + ending.length);
                resolve(ending); // Resolve with just the ending marker
            } else {
                // Ending not found yet. Forward all buffered data except the last
                // (ending.length - 1) characters, which could be a partial match
                // of the ending sequence split across chunks.
                const safeLength = Math.max(
                    0,
                    this.buffer.length - (ending.length - 1),
                );
                if (safeLength > 0) {
                    const safe = this.buffer.substring(0, safeLength);
                    onChunk(safe);
                    this.buffer = this.buffer.substring(safeLength);
                }
            }
        }
    }

    private escapeForLog(str: string): string {
        let result = '';
        for (const ch of str) {
            const code = ch.charCodeAt(0);
            if (code < 0x20) {
                result += '\\x' + code.toString(16).padStart(2, '0');
            } else {
                result += ch;
            }
        }
        return result;
    }
}

// --- Internal Types ---

interface PendingReadUntil {
    type: 'until';
    ending: string;
    resolve: (data: string) => void;
    reject: (err: Error) => void;
}

interface PendingReadExactly {
    type: 'exactly';
    count: number;
    resolve: (data: string) => void;
    reject: (err: Error) => void;
}

interface PendingReadStreaming {
    type: 'streaming';
    ending: string;
    accumulated: string;
    onChunk: (chunk: string) => void;
    resolve: (data: string) => void;
    reject: (err: Error) => void;
}

type PendingRead = PendingReadUntil | PendingReadExactly | PendingReadStreaming;
